// ==========================================================================
// EXCEL IMPORT — extraction par libellé (colonne A) des onglets champ/valeur
// du template HardToValue (1_Identification, 5_Paramètres_Marché).
//
// Principe : on ne mappe plus par offset de ligne. Chaque champ est retrouvé
// par correspondance de son libellé (normalisé : casse, accents, séparateur
// FR/EN). Les lignes de section (majuscules, sans valeur dans le template
// vierge) sont ignorées ; si une ligne de section porte une valeur en
// colonne C, c'est la signature d'un classeur généré avec un décalage d'une
// ligne (la section traitée comme ligne de données) — le bloc concerné est
// alors relu avec un offset de −1 et signalé dans `shiftedSections`.
// ==========================================================================
import * as XLSX from "xlsx-js-style";

export function xlCellAt(sheet, r, c) {
  if (!sheet || r == null || c == null) return null; // ligne non résolue → null
  const cell = sheet[XLSX.utils.encode_cell({ r, c })];
  if (!cell) return null;
  const v = cell.v;
  if (v === null || v === undefined || v === "") return null;
  return v;
}

export const asNum = (v) => {
  if (typeof v === "number") return isFinite(v) ? v : null;
  if (typeof v === "string" && v.trim() !== "") {
    const n = Number(v.trim().replace(/\s/g, "").replace(",", "."));
    return isFinite(n) ? n : null;
  }
  return null;
};

export const asStr = (v) => (v == null ? "" : String(v).trim());

// Nombre saisi en TEXTE : la donnée est bonne (coercible), mais la cellule est
// typée texte — signature d'un générateur qui écrit du texte là où il devrait
// écrire un nombre. Sert au SIGNAL (pas au rejet) sur les scores de Porter.
export const isTextNumber = (v) => typeof v === "string" && asNum(v) != null;

// Résolution d'un paramètre importé : distingue VIDE (défaut appliqué + signalé)
// de ZÉRO (valeur délibérée conservée). Le `|| défaut` confondait les deux : un
// 0 saisi (prime de contrôle nulle, décote nulle) devenait le défaut ; pire, un
// Rf/ERP absent tombait sur un défaut au lieu d'être signalé. Prérequis vérifié
// en amont : la chaîne de lecture renvoie null pour vide, 0 pour zéro (asNum).
// `value` est déjà le retour d'asNum (null = absent, nombre = présent, 0 inclus).
export function resolveParam(value, def) {
  return value == null
    ? { value: def, defaulted: true }   // absent → défaut, à signaler
    : { value, defaulted: false };       // présent (0 compris) → conservé
}

// Ratio à dénominateur strictement positif. Règle unique du pipeline : si le
// dénominateur est ≤ 0 (ou absent), on retourne null — jamais 0, jamais
// Infinity, jamais une valeur négative propagée. L'affichage rend « n.s. ».
export const safeRatio = (num, den) =>
  num == null || den == null || !isFinite(num) || !isFinite(den) || den <= 0
    ? null
    : num / den;

// Lignes du compte de résultat (index 0-based) — alignées sur le template
// HardToValue (3_Compte_Résultat). Les numéros de ligne du brief (l.4, l.7…)
// sont les lignes Excel 1-based correspondantes (l.4 = CA = index 3).
// Index positionnels de REPLI (0-based) — layout de référence du template.
// Utilisés uniquement si la résolution par libellé échoue (voir resolvePlRows).
export const PL_ROW = {
  revenue: 3, achats: 6, personnel: 7, remun: 8, impots: 9, autres: 10,
  da: 14, prov: 15, fin: 18, ebt: 19, tax: 21, ni: 22, div: 24,
  capex: 27, dnwc: 28,
};

// C1 — robustesse : le compte de résultat était lu par INDICE codé en dur.
// Le motif exact des deux bugs (1_Identification, 4_Bilan) était un décalage de
// LIGNE. On résout donc chaque poste par son libellé colonne A, avec repli sur
// l'indice positionnel si le libellé est introuvable (aucune régression).
const PL_ROW_MATCHERS = {
  revenue:   [/chiffre d affaires/, /\brevenue\b/, /\bturnover\b/],
  achats:    [/achats/, /purchases/, /external charges/],
  personnel: [/charges de personnel/, /staff costs/, /payroll/],
  remun:     [/remuneration dirigeant/, /owner comp/, /owner salary/],
  impots:    [/impots (et |& )?taxes/, /taxes (et |& )?duties/, /non.?income taxes/],
  autres:    [/autres charges d exploitation/, /other opex/],
  da:        [/dotations amortissements/, /\bd ?& ?a\b/, /depreciation/, /amortization/],
  prov:      [/dotations provisions/, /provision charges/, /^provisions/],
  fin:       [/charges financieres/, /financial charges/, /interest expense/],
  ebt:       [/\bebt\b/, /resultat avant impot/, /pre.?tax/],
  tax:       [/impot sur les societes/, /corporate tax/, /income tax/],
  ni:        [/resultat net/, /net income/],
  div:       [/dividendes/, /dividends/],
  capex:     [/investissements/, /\bcapex\b/, /capital expenditure/],
  dnwc:      [/variation bfr/, /change in nwc/, /working capital change/],
};

// Index 0-based de la première ligne DE DONNÉES dont le libellé (col A,
// normalisé) matche. Les lignes de SECTION (majuscules : « PRODUITS / REVENUE »,
// « IMPÔT & RÉSULTAT NET / TAX & NET INCOME ») sont ignorées — sinon un matcher
// large les capterait avant la vraie ligne de poste.
export function rowIndexByLabel(sheet, matchers, { maxRow = 300 } = {}) {
  if (!sheet || !sheet["!ref"]) return null;
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const end = Math.min(range.e.r, maxRow);
  for (let r = range.s.r; r <= end; r++) {
    const label = xlCellAt(sheet, r, 0);
    if (typeof label !== "string" || !label.trim()) continue;
    if (isSectionLabel(label)) continue; // saute les en-têtes de section
    const norm = normLabel(label);
    if (matchers.some((re) => re.test(norm))) return r;
  }
  return null;
}

// Résout les lignes du compte de résultat PAR LIBELLÉ. AUCUN repli positionnel :
// un poste introuvable ressort à null (jamais un indice fixe). Le repli
// supposerait le layout de référence exactement quand le libellé manque —
// c'est-à-dire quand le classeur a le plus de chances de NE PAS le suivre : il
// lirait alors une ligne fausse en silence. À la place, on remonte la liste des
// postes non résolus pour le rapport d'import (cohérent avec CapEx/ΔBFR absents).
export function resolvePlRows(sheet) {
  const rows = {};
  const unresolved = [];
  for (const key of Object.keys(PL_ROW)) {
    const r = rowIndexByLabel(sheet, PL_ROW_MATCHERS[key]);
    rows[key] = r; // index ou null
    if (r == null) unresolved.push(key);
  }
  return { rows, unresolved };
}

// Dérive EBITDA / EBIT par colonne à partir des postes du compte de résultat,
// au lieu de lire les lignes EBITDA/EBIT du classeur (dérivées, vides, non
// mises en cache → sinon lues à 0).
//
// Convention ARRÊTÉE : EBITDA BRUT — la rémunération dirigeant est DÉDUITE
// (arithmétique nue du compte de résultat). Sa réintégration éventuelle est un
// RETRAITEMENT porté par une grandeur nommée distinctement (voir
// `normalizedEbitda`), jamais fondu dans la base d'import.
//
//   EBITDA = CA − Achats − Personnel − Rémun. dirigeant − Impôts&taxes − Autres opex
//   EBIT   = EBITDA − D&A − Provisions
//
// La marge est rapportée au CA (dénominateur > 0) : elle reste définie et
// négative quand l'EBITDA est négatif. Les composantes de charges absentes
// comptent pour 0 ; un CA absent rend toute la colonne null.
export function deriveIncomeStatement(sheet, colIndices, factor = 1, rows = PL_ROW) {
  const num = (row, c) => {
    const v = xlCellAt(sheet, row, c);
    return typeof v === "number" && isFinite(v) ? v * factor : null;
  };
  const R = rows;
  return colIndices.map((c) => {
    const revenue = num(R.revenue, c);
    if (revenue == null) {
      return { revenue: null, ebitda: null, ebit: null, ebitda_margin: null,
        ebit_margin: null, remun: null, da: null, prov: null };
    }
    const achats = num(R.achats, c) || 0;
    const personnel = num(R.personnel, c) || 0;
    const remun = num(R.remun, c) || 0;
    const impots = num(R.impots, c) || 0;
    const autres = num(R.autres, c) || 0;
    const da = num(R.da, c) || 0;
    const prov = num(R.prov, c) || 0;
    const ebitda = revenue - achats - personnel - remun - impots - autres;
    const ebit = ebitda - da - prov;
    return {
      revenue, ebitda, ebit,
      ebitda_margin: safeRatio(ebitda, revenue),
      ebit_margin: safeRatio(ebit, revenue),
      remun, da, prov,
    };
  });
}

// EBITDA NORMALISÉ — grandeur distincte, jamais substituée à l'EBITDA brut.
// Réintègre la rémunération dirigeant (poste « owner salary to add back »).
// Retourne null si l'EBITDA brut est null.
export const normalizedEbitda = (grossEbitda, remun) =>
  grossEbitda == null ? null : grossEbitda + (remun || 0);

// Décomposition du WACC par build-up (CAPM + Hamada). Reproduit la traçabilité
// « fin de la black box » : β relevé, Ke, Kd net, WACC. Alimente le panneau
// build-up à l'import (source « Calculé »), jamais le slot de surcharge client.
export function computeWaccBuildup({ rf, erp, beta_u, size_prem = 0, spec_prem = 0, kd_pretax, tax_wacc, eq_weight }) {
  const d_over_e = eq_weight > 0 ? (1 - eq_weight) / eq_weight : 0.3;
  const beta_lev = beta_u * (1 + (1 - tax_wacc) * d_over_e);
  const ke = rf + beta_lev * erp + size_prem + spec_prem;
  const kd_net = kd_pretax * (1 - tax_wacc);
  const wacc = eq_weight * ke + (1 - eq_weight) * kd_net;
  return { d_over_e, beta_lev, ke, kd_net, wacc };
}

// RÈGLE DE PRÉCÉDENCE (unique pour EBITDA, CapEx, ΔBFR) :
//   1. valeur EXPLICITE dans la cellule du classeur → retenue telle quelle
//      (source "saisie") ;
//   2. à défaut, valeur DÉRIVÉE d'autres postes du classeur (source "dérivé") ;
//   3. à défaut, ESTIMÉE via un ratio de 5_Paramètres_Marché (source "estimé") ;
//   4. à défaut, null + avertissement (source "absent") — jamais un zéro muet.
// La source retenue est toujours renvoyée pour être affichée et estampillée à
// l'export. En aval, une saisie utilisateur dans le modèle prime sur tout ceci.
export function resolveWithPrecedence({ cell, derived, ratioValue, label }) {
  if (cell != null) return { value: cell, source: "saisie", label };
  if (derived != null) return { value: derived, source: "dérivé", label };
  if (ratioValue != null) return { value: ratioValue, source: "estimé", label };
  return { value: null, source: "absent", label };
}

// Construit les projections client (N+1..N+5) depuis 3_Compte_Résultat.
// CapEx / ΔBFR : lignes dérivées vides dans le template → précédence
// cellule > ratio (5_Paramètres_Marché) > absent. Renvoie les projections + la
// provenance par ligne (estimé / absent) pour l'avertissement d'import.
export function buildClientProjections(sheet, { factor = 1, baseRevenue = null, capexRatio = null, nwcRatio = null, ebitdaByCol = [], plRows = PL_ROW } = {}) {
  const num = (row, c) => {
    const v = xlCellAt(sheet, row, c);
    return typeof v === "number" && isFinite(v) ? v * factor : null;
  };
  const R = plRows;
  const rows = [];
  const estimated = new Set();
  const missing = new Set();
  // Colonnes de projection H..L = index 7..11 (N+1..N+5) ; ebitdaByCol aligné N..N+5.
  // Le CA de l'année précédente sert au calcul du ΔBFR ; pour N+1 c'est le CA de N.
  let prevRevenue = baseRevenue;
  for (let ci = 0; ci < 5; ci++) {
    const c = 7 + ci;
    const revenue = num(R.revenue, c);
    if (revenue == null) break;
    // CapEx : ratio de FLUX (CapEx annuel / CA annuel) → ratio × CA_t.
    const capex = resolveWithPrecedence({
      cell: num(R.capex, c), ratioValue: capexRatio != null ? Math.round(revenue * capexRatio) : null,
      label: capexRatio != null ? `CapEx (${(capexRatio * 100).toFixed(1)} % du CA)` : "CapEx",
    });
    // ΔBFR : le ratio saisi est une INTENSITÉ de niveau (BFR/CA), pas un flux.
    // La VARIATION de BFR ≈ intensité × VARIATION du CA → ratio × (CA_t − CA_t-1),
    // et non ratio × CA_t (qui impliquerait une ponction de 14 % du CA chaque
    // année indéfiniment, impossible en régime permanent).
    const deltaCA = prevRevenue != null ? revenue - prevRevenue : null;
    const dnwc = resolveWithPrecedence({
      cell: num(R.dnwc, c),
      ratioValue: (nwcRatio != null && deltaCA != null) ? Math.round(nwcRatio * deltaCA) : null,
      label: nwcRatio != null ? `ΔBFR (${(nwcRatio * 100).toFixed(1)} % de la variation du CA)` : "ΔBFR",
    });
    if (capex.source === "estimé") estimated.add(capex.label);
    if (capex.source === "absent") missing.add("CapEx");
    if (dnwc.source === "estimé") estimated.add(dnwc.label);
    if (dnwc.source === "absent") missing.add("ΔBFR");
    rows.push({
      revenue,
      ebitda: ebitdaByCol[ci + 1] ?? null,
      da: num(R.da, c),
      capex: capex.value, capex_source: capex.source,
      delta_nwc: dnwc.value, delta_nwc_source: dnwc.source,
    });
    prevRevenue = revenue;
  }
  return { rows, estimatedLines: [...estimated], missingLines: [...missing] };
}

// Normalisation d'un libellé : accents, casse, ponctuation → mots séparés
// par un espace. "Type d'opération / Operation type" → "type d operation operation type"
export const normLabel = (s) =>
  String(s)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

// Ligne de section : libellé essentiellement en majuscules
// (ex. "INFORMATIONS SOCIÉTÉ / COMPANY INFORMATION", "COÛT DU CAPITAL / WACC").
export function isSectionLabel(v) {
  if (typeof v !== "string") return false;
  const letters = v.replace(/[^A-Za-zÀ-ÖØ-öø-ÿ]/g, "");
  if (letters.length < 3) return false;
  const upper = letters.replace(/[a-zà-öø-ÿ]/g, "");
  return upper.length / letters.length >= 0.8;
}

// Lit un onglet champ/valeur → entrées {row, label, norm, value, section,
// shifted} + liste des sections décalées. La détection du décalage est faite
// bloc par bloc : une section qui porte une valeur en colonne `valueCol`
// décale tout son bloc de −1. Chaque entrée porte le libellé de sa section et
// un drapeau `shifted` pour permettre à l'appelant de savoir quels champs ont
// été réinterprétés.
export function readLabelledSheet(sheet, { valueCol = 2, maxRow = 300 } = {}) {
  const entries = [];
  const shiftedSections = [];
  if (!sheet || !sheet["!ref"]) return { entries, shiftedSections };
  const range = XLSX.utils.decode_range(sheet["!ref"]);
  const end = Math.min(range.e.r, maxRow);
  let shift = 0;
  let section = null;
  for (let r = range.s.r; r <= end; r++) {
    const label = xlCellAt(sheet, r, 0);
    if (typeof label !== "string" || !label.trim()) continue;
    if (isSectionLabel(label)) {
      section = label.trim();
      shift = xlCellAt(sheet, r, valueCol) != null ? -1 : 0;
      if (shift === -1) shiftedSections.push(section);
      continue;
    }
    entries.push({
      row: r,
      label: label.trim(),
      norm: normLabel(label),
      value: xlCellAt(sheet, r + shift, valueCol),
      section,
      shifted: shift === -1,
    });
  }
  return { entries, shiftedSections };
}

// Associe les entrées d'un onglet à une spécification de champs.
// specs : [{ key, label?, match: [RegExp…], exclude?: RegExp }] — testés sur le
// libellé normalisé, première entrée correspondante retenue.
export function pickFields(entries, specs) {
  const values = {};
  const matched = {}; // key → entrée retenue
  const matchedRows = new Set();
  for (const { key, match, exclude } of specs) {
    for (const e of entries) {
      if (matchedRows.has(e.row)) continue;
      if (exclude && exclude.test(e.norm)) continue;
      if (match.some((re) => re.test(e.norm))) {
        values[key] = e.value;
        matched[key] = e;
        matchedRows.add(e.row);
        break;
      }
    }
    if (!(key in values)) values[key] = null;
  }
  const unrecognized = entries
    .filter((e) => !matchedRows.has(e.row) && e.value != null)
    .map((e) => e.label);
  return { values, matched, unrecognized };
}

// Construit, à partir des champs retenus, la liste des champs issus d'un bloc
// décalé, regroupés par section : [{ section, fields: [libellés lisibles] }].
// `fieldLabels` : key → libellé court affichable (ex. "Société").
export function shiftedFieldsBySection(matched, fieldLabels) {
  const bySection = new Map();
  for (const [key, e] of Object.entries(matched)) {
    if (!e || !e.shifted) continue;
    const label = fieldLabels[key] || e.label;
    if (!bySection.has(e.section)) bySection.set(e.section, []);
    bySection.get(e.section).push(label);
  }
  return [...bySection.entries()].map(([section, fields]) => ({ section, fields }));
}

// ── 1_Identification ──────────────────────────────────────────────────────
const ID_FIELDS = [
  { key: "societe",         match: [/\bsociete\b/, /company name/] },
  { key: "secteur",         match: [/\bsecteur\b/, /industry/] },
  { key: "pays",            match: [/\bpays\b/, /\bcountry\b/] },
  { key: "forme_juridique", match: [/forme juridique/, /legal form/] },
  { key: "type_operation",  match: [/type d operation/, /operation type/] },
  { key: "juridiction",     match: [/juridiction/, /jurisdiction/] },
  { key: "date_cloture",    match: [/date de cloture/, /fiscal year end/] },
  { key: "devise",          match: [/\bdevise\b/, /currency/] },
  { key: "exercice",        match: [/exercice/, /fiscal year(?! end)/], exclude: /cloture|year end/ },
  { key: "facteur",         match: [/facteur/, /unit factor/] },
  { key: "nombre_titres",   match: [/nombre de titres/, /share count/, /shares outstanding/] },
  { key: "analyste",        match: [/analyste/, /analyst/] },
  { key: "ref_engagement",  match: [/engagement/] },
];

const ID_LABELS = {
  societe: "Société", secteur: "Secteur", pays: "Pays",
  forme_juridique: "Forme juridique", type_operation: "Type d'opération",
  juridiction: "Juridiction", date_cloture: "Date de clôture",
  devise: "Devise", exercice: "Exercice fiscal",
  facteur: "Facteur d'unité", nombre_titres: "Nombre de titres",
  analyste: "Analyste", ref_engagement: "Réf. engagement",
};

export function readIdentification(sheet) {
  const { entries, shiftedSections } = readLabelledSheet(sheet);
  const { values, matched, unrecognized } = pickFields(entries, ID_FIELDS);
  const shiftedFields = shiftedFieldsBySection(matched, ID_LABELS);
  return { values, shiftedSections, shiftedFields, unrecognized };
}

// ── Contrôle de type par champ (successeur robuste de la signature de section) ─
// Un contrôle de type ne détecte pas un décalage directement : il le détecte
// PAR RICOCHET, parce qu'une valeur atterrit dans un champ dont elle viole le
// domaine. Seuls les champs à DOMAINE ÉTROIT peuvent parler (devise, exercice,
// date, forme juridique) — Société/Secteur acceptent n'importe quelle chaîne
// et resteront muets. D'où l'ESCALADE : plusieurs champs à domaine étroit
// invalides dans un même bloc ⇒ ce n'est presque jamais une cellule fautive,
// c'est le bloc entier qui est décalé. Le message doit le dire, sinon
// l'utilisateur corrige une cellule et laisse les huit autres fausses.
//
// Formes juridiques : la plateforme porte des onglets US/DE/CH/UK — la liste
// est INTERNATIONALE et le contrôle reste un AVERTISSEMENT, jamais un rejet.
const LEGAL_FORMS = new Set([
  // FR
  "SAS","SASU","SARL","EURL","SA","SNC","SCA","SCS","SEL","SELARL","SCI","GIE","SCP","SEM","SE",
  // DE / AT
  "GMBH","AG","UG","KG","OHG","KGAA","GBR","EG","MBH","GMBHCOKG",
  // CH (SÀRL→SARL après normalisation)
  // UK / IE
  "LTD","PLC","LLP","LP","CIC",
  // US
  "LLC","INC","CORP","CO","PC","PLLC",
  // NL / BE
  "BV","NV","BVBA","CVBA","VOF",
  // ES
  "SL","SLU","SLNE","SAU",
  // IT
  "SPA","SRL","SASIT","SAPA","SRLS",
  // Scandinavie / autres
  "OY","OYJ","AB","AS","ASA","APS","AMBA","LTDA","LDA","PTY","PTYLTD",
  "KFT","ZRT","NYRT","DOO","SP","SPZOO","OOO","PAO","AO",
]);
const normForm = (s) => String(s ?? "").normalize("NFD").replace(/[̀-ͯ]/g, "")
  .toUpperCase().replace(/[^A-Z0-9]/g, "");

export function checkFieldTypes(values, blockLabel = "INFORMATIONS SOCIÉTÉ") {
  const issues = [];
  const present = (v) => v != null && String(v).trim() !== "";

  // Devise — code ISO 4217 alpha-3 (EUR, USD, GBP, CHF…). Reste un domaine
  // strict : « 2026 » n'est pas une devise. Non-euro géré par le domaine lui-même.
  if (present(values.devise) && !/^[A-Za-z]{3}$/.test(String(values.devise).trim())) {
    issues.push({ field: "devise", label: "Devise", value: values.devise, level: "error",
      message: `Devise « ${values.devise} » : attendu un code ISO à 3 lettres (EUR, USD, GBP…).` });
  }
  // Exercice — année à 4 chiffres
  const y = asNum(values.exercice);
  if (present(values.exercice) && (y == null || !Number.isInteger(y) || y < 1900 || y > 2100)) {
    issues.push({ field: "exercice", label: "Exercice fiscal", value: values.exercice, level: "error",
      message: `Exercice « ${values.exercice} » : attendu une année à 4 chiffres.` });
  }
  // Date de clôture — LENIENT : JJ/MM, JJ/MM/AAAA, ou date Excel (nombre/Date).
  // Le format saisi varie légitimement ; on ne rejette que le hors-domaine franc.
  const d = values.date_cloture;
  const dateOk = typeof d === "number" || d instanceof Date ||
    (present(d) && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(String(d).trim()));
  if (present(d) && !dateOk) {
    issues.push({ field: "date_cloture", label: "Date de clôture", value: d, level: "error",
      message: `Date de clôture « ${d} » : format attendu JJ/MM (ou date).` });
  }
  // Forme juridique — liste internationale, AVERTISSEMENT jamais rejet
  if (present(values.forme_juridique) && !LEGAL_FORMS.has(normForm(values.forme_juridique))) {
    issues.push({ field: "forme_juridique", label: "Forme juridique", value: values.forme_juridique, level: "warning",
      message: `Forme juridique « ${values.forme_juridique} » non reconnue (SAS, GmbH, Ltd, Inc, BV…) — à vérifier.` });
  }

  // Escalade champ → bloc : ≥ 2 champs à domaine étroit INVALIDES (error) dans
  // le même bloc → suspicion de décalage, pas de cellules isolées.
  const hardErrors = issues.filter((i) => i.level === "error");
  const shiftSuspected = hardErrors.length >= 2;
  const shiftMessage = shiftSuspected
    ? `Bloc ${blockLabel} probablement décalé d'une ligne : ${hardErrors.map((i) => i.label).join(", ")} portent des valeurs hors de leur domaine. Vérifiez TOUT le bloc (Société, Secteur, Pays…) — les champs à texte libre ne peuvent pas s'auto-détecter.`
    : null;
  return { issues, shiftSuspected, shiftMessage };
}

// ── 5_Paramètres_Marché ───────────────────────────────────────────────────
const MKT_FIELDS = [
  { key: "rf",           match: [/taux sans risque/, /risk free/] },
  { key: "erp",          match: [/prime de risque actions/, /equity risk premium/] },
  { key: "beta_u",       match: [/beta desendette/, /beta non endette/, /unlevered beta/] },
  { key: "size_prem",    match: [/prime de taille/, /size premium/] },
  { key: "spec_prem",    match: [/prime specifique/, /specific premium/] },
  { key: "kd_pretax",    match: [/cout de la dette/, /cout dette/, /cost of debt/] },
  { key: "taux_is_vm",   match: [/valeur math/, /math value/] },
  { key: "tax_wacc",     match: [/taux is/, /corporate tax/], exclude: /math/ },
  { key: "eq_weight",    match: [/ponderation equity/, /equity weight/] },
  { key: "inflation",    match: [/^inflation$/] },
  { key: "div_yield",    match: [/rendement dividende/, /dividend yield/] },
  { key: "dlom",         match: [/\bdlom\b/, /illiquidity/, /decote de non liquidite/] },
  { key: "ctrl_prem",    match: [/prime de controle/, /control premium/] },
  { key: "size_disc",    match: [/decote de taille/, /size discount/] },
  { key: "beta_dgi",     match: [/beta dgi/, /dgi beta/] },
  { key: "client_wacc",  match: [/wacc fourni par le client/, /client provided wacc/, /client wacc/] },
  // Hypothèses business plan (phases ultérieures : CapEx/ΔBFR, croissance, TV)
  { key: "growth_n1",    match: [/croissance ca n 1\b/, /revenue growth n 1\b/] },
  { key: "growth_n2",    match: [/croissance ca n 2\b/, /revenue growth n 2\b/] },
  { key: "growth_n3",    match: [/croissance ca n 3\b/, /revenue growth n 3\b/] },
  { key: "growth_n4",    match: [/croissance ca n 4\b/, /revenue growth n 4\b/] },
  { key: "growth_n5",    match: [/croissance ca n 5\b/, /revenue growth n 5\b/] },
  { key: "target_ebitda_margin", match: [/marge ebitda cible/, /target ebitda margin/] },
  { key: "capex_ratio",  match: [/ratio capex/] },
  { key: "nwc_ratio",    match: [/ratio var bfr/, /working capital/] },
  { key: "terminal_growth", match: [/croissance terminal/, /terminal growth/] },
];

const MKT_LABELS = {
  rf: "Taux sans risque", erp: "Prime de risque actions", beta_u: "Bêta désendetté",
  size_prem: "Prime de taille", spec_prem: "Prime spécifique", kd_pretax: "Coût de la dette",
  taux_is_vm: "Taux IS (valeur math.)", tax_wacc: "Taux IS", eq_weight: "Pondération equity",
  inflation: "Inflation", div_yield: "Rendement dividende", dlom: "DLOM",
  ctrl_prem: "Prime de contrôle", size_disc: "Décote de taille", beta_dgi: "Bêta DGI",
  client_wacc: "WACC client", terminal_growth: "Croissance terminale",
  capex_ratio: "Ratio Capex/CA", nwc_ratio: "Ratio ΔBFR/CA",
  target_ebitda_margin: "Marge EBITDA cible",
};

export function readMarketParams(sheet) {
  const { entries, shiftedSections } = readLabelledSheet(sheet);
  const { values, matched, unrecognized } = pickFields(entries, MKT_FIELDS);
  const shiftedFields = shiftedFieldsBySection(matched, MKT_LABELS);
  return { values, shiftedSections, shiftedFields, unrecognized };
}

// ── 4_Bilan ───────────────────────────────────────────────────────────────
// La valeur de l'exercice N est en colonne D (index 3) ; C = N-1. Lecture par
// libellé : les indices positionnels étaient calibrés sur un autre layout et
// décalaient tout le passif d'une ligne (capitaux propres lus en provisions,
// fournisseurs comptés comme dette financière).
const BS_FIELDS = [
  { key: "immob_inc",       match: [/immob.*incorporel/, /intangible/] },
  { key: "goodwill",        match: [/goodwill/, /fonds commercial/] },
  { key: "immob_corp",      match: [/immob.*corporel/, /tangible/] },
  { key: "stocks",          match: [/\bstocks\b/, /inventor/] },
  { key: "creances",        match: [/creances clients/, /trade receivable/] },
  { key: "autres_creances", match: [/autres creances/, /other receivable/] },
  { key: "cash",            match: [/tresorerie/, /\bcash\b/] },
  { key: "capitaux_propres",match: [/capitaux propres/, /\bequity\b/], exclude: /passif|liabilit/ },
  { key: "provisions",      match: [/provisions/, /provision/] },
  { key: "dettes_lt",       match: [/dettes lt/, /long.?term debt/] },
  { key: "dettes_ct",       match: [/dettes ct/, /short.?term debt/] },
  { key: "fournisseurs",    match: [/fournisseurs/, /trade payable/] },
  { key: "autres_dettes",   match: [/autres dettes/, /other liabilit/] },
];

// Capitaux propres ≤ 0 : le RATIO (gearing, ROE) reste à null — il n'a pas de
// sens — MAIS la situation nette négative est en soi une information majeure sur
// la santé de la société. Elle doit remonter comme CONSTAT explicite, pas se
// traduire par une case vide indiscernable d'une donnée manquante.
export const isNegativeEquity = (capitauxPropres) =>
  capitauxPropres != null && capitauxPropres <= 0;

export function readBalanceSheet(sheet) {
  const { entries, shiftedSections } = readLabelledSheet(sheet, { valueCol: 3 });
  const { values, matched, unrecognized } = pickFields(entries, BS_FIELDS);
  const shiftedFields = shiftedFieldsBySection(matched, {});
  return { values, shiftedSections, shiftedFields, unrecognized };
}

// Contrôle d'intégrité comptable — vérification de CONTENU, indépendante de la
// mise en page (comme le contrôle de type). Une ligne d'arithmétique : Actif =
// Passif sur chaque colonne renseignée, et BFR recalculé vs poste dérivé s'il
// est présent. Signale les classeurs dont les données ne se réconcilient pas —
// leurs sorties de valorisation sont justes en calcul mais fausses en substance.
const BS_ASSET_KEYS = ["immob_inc", "goodwill", "immob_corp", "stocks", "creances", "autres_creances", "cash"];
const BS_LIAB_KEYS  = ["capitaux_propres", "provisions", "dettes_lt", "dettes_ct", "fournisseurs", "autres_dettes"];

// Deux régimes, pour qu'un garde-fou qui crie sur des arrondis ne finisse pas
// ignoré puis désactivé :
//   • écart STRUCTUREL (> seuil) → severity "structural" (rouge) ;
//   • écart d'ARRONDI (au-dessus du bruit, sous le seuil) → severity "minor"
//     (mention discrète).
//
// ⚠ CALIBRAGE DU SEUIL — ne pas « corriger » vers 1-2 % du total actif.
// Ce seuil ne mesure PAS l'importance relative d'audit (qui juge si une
// anomalie affecte le jugement d'un lecteur des comptes). Il distingue le
// BRUIT D'ARRONDI DE SAISIE d'un vrai déséquilibre : un bilan boucle ou ne
// boucle pas, et la seule tolérance légitime vient des arrondis. Or ce bruit
// dépend du nombre de lignes additionnées et de l'unité de saisie (une dizaine
// de postes en k€ → quelques k€), PAS de la taille du bilan. Le PLANCHER
// ABSOLU domine donc, et le pourcentage (0,1 %) n'est qu'un garde-fou pour les
// très grands bilans (saisis en M€, où l'arrondi unitaire pèse plus lourd).
// Avec 1 % « d'usage », un bilan qui ne boucle pas à 200 k€ près serait classé
// « arrondi » — ce n'est pas un arrondi, c'est un poste manquant.
export function checkBalanceIntegrity(sheet, {
  factor = 1, materialityPct = 0.001, absoluteFloor = 10000, noiseFloor = 1,
} = {}) {
  const warnings = [];
  if (!sheet || !sheet["!ref"]) return { warnings };
  const classify = (gapAbs, scale) => {
    if (gapAbs <= noiseFloor) return null; // équilibré à l'euro près
    const materiality = Math.max(materialityPct * scale, absoluteFloor);
    return gapAbs > materiality ? "structural" : "minor";
  };
  // Colonnes : N-1 = C (2), N = D (3). Valeurs ramenées en unités de base (×facteur).
  for (const { label, c } of [{ label: "N-1", c: 2 }, { label: "N", c: 3 }]) {
    const { entries } = readLabelledSheet(sheet, { valueCol: c });
    const { values } = pickFields(entries, BS_FIELDS);
    const asset = BS_ASSET_KEYS.reduce((s, k) => s + (asNum(values[k]) || 0), 0) * factor;
    const liab  = BS_LIAB_KEYS.reduce((s, k) => s + (asNum(values[k]) || 0), 0) * factor;
    if (asset === 0 && liab === 0) continue; // colonne vide → rien à vérifier
    const gap = asset - liab;
    const sev = classify(Math.abs(gap), Math.max(Math.abs(asset), Math.abs(liab)));
    if (sev) warnings.push({ type: "balance", severity: sev, column: label, asset, liab, gap });
    // BFR recalculé (stocks + créances − fournisseurs) vs poste dérivé s'il existe
    const bfrDerived = asNum(pickFields(entries, [{ key: "bfr", match: [/\bbfr\b/, /\bnwc\b/] }]).values.bfr);
    if (bfrDerived != null) {
      const bfrCalc = ((asNum(values.stocks) || 0) + (asNum(values.creances) || 0) - (asNum(values.fournisseurs) || 0));
      const dcalc = bfrCalc * factor, dder = bfrDerived * factor;
      const sevB = classify(Math.abs(dcalc - dder), Math.max(Math.abs(dder), Math.abs(dcalc)));
      if (sevB) warnings.push({ type: "bfr", severity: sevB, column: label, derived: dder, computed: dcalc });
    }
  }
  return { warnings };
}
